import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/ui/button";
import { Message } from "@/ui/message";
import { Spinner } from "@/ui/spinner";
import { Ausencia, Botao, Esqueleto, Etiqueta, Fechar, Marcador, SeloIA, Trabalhando } from "../componentes/comuns.jsx";
import { Icone, ICONES, hora } from "../visual.jsx";
import { useAcoes, useOcupado } from "../estado.jsx";
import { BotaoDoComposer, Composer } from "../componentes/Composer.jsx";
import * as api from "../api.js";

/**
 * O chat da peça: a coluna lateral do editor.
 *
 * Era uma lista de fatos e um botão. Isso resolvia UM pedido e escondia o resto
 * do que a casa sabe fazer — ajustar o trecho marcado, procurar no acervo de
 * peças protocoladas, ir perguntar ao cliente o que falta. Quem não sabia que
 * dava, não pedia; e o que se pediu ontem não ficava em lugar nenhum.
 *
 * Três decisões de desenho sustentam a coluna:
 *
 * 1. **O catálogo é visível.** Os comandos são pastilhas com nome e dica, não
 *    um menu escondido atrás de "/" — quem revisa peça não decora sintaxe.
 * 2. **O trecho marcado aparece ANTES do envio.** É o "isto" de "reescreva
 *    isto": sem mostrar o que está marcado, a pessoa pede no escuro.
 * 3. **Nada é aplicado sozinho.** A IA propõe; a bolha traz as alterações uma a
 *    uma, com "aplicar" em cada — o .docx é o que vai ao fórum.
 */

/** O ícone de cada comando, para a pastilha ser reconhecida sem ler. */
const GLIFO = {
  ajustar: ICONES.ia,
  fatos: ICONES.nota,
  acervo: ICONES.busca,
  perguntar: ICONES.whatsapp,
  anexo: ICONES.anexo,
};

/** A pele de cada papel na conversa. */
/**
 * A pele da bolha, em token e em classe — nunca em estilo calculado.
 *
 * Era `s()` com `background`, `color` e `border` montados em texto: a mesma
 * bolha da conversa dos assistentes já se pintava por classe, e duas receitas
 * para a mesma peça é como a paleta volta a se espalhar.
 */
function peleDe(papel) {
  if (papel === "pessoa") return { classe: "border-transparent bg-verde-fundo text-creme", lado: "end" };
  if (papel === "sistema") return { classe: "border-terra/30 bg-terra/10 text-terra-escura", lado: "start" };
  return { classe: "border-foreground/10 bg-papel text-tinta", lado: "start" };
}

/** Uma alteração proposta pela IA, com o botão que a escreve na peça. */
function Edicao({ edicao, aplicada, aoAplicar }) {
  return (
    <div className="mt-2 rounded-[10px] border border-foreground/12 bg-foreground/2 p-[9px_10px]">
      <div className="font-mono text-selo font-bold tracking-[.06em] text-muted-foreground">TROCAR</div>
      <div className="mt-1 text-apoio leading-[1.45] text-tinta-suave line-through [overflow-wrap:anywhere]">
        {edicao.procurar}
      </div>
      <div className="mt-1.5 font-mono text-selo font-bold tracking-[.06em] text-verde-fundo">POR</div>
      <div className="mt-1 text-apoio leading-[1.45] [overflow-wrap:anywhere]">{edicao.substituir || "(apagar)"}</div>
      {edicao.motivo ? <div className="mt-1.5 text-apoio leading-[1.45] text-muted-foreground">{edicao.motivo}</div> : null}
      <div className="mt-2">
        <Botao
          rotulo={aplicada ? "Aplicada" : "Aplicar na peça"}
          desabilitado={aplicada}
          titulo={aplicada ? "Esta alteração já foi escrita no documento" : "Escreve esta troca no .docx"}
          aoTocar={aoAplicar}
        />
      </div>
    </div>
  );
}

/** Os fatos que o comando devolveu, com o marcador de "entra na peça". */
function FatosDaBolha({ ids, provas, aoAlternar, aoVerNaConversa, aoInserir }) {
  const daBolha = (provas ?? []).filter((p) => (ids ?? []).includes(p.id));
  if (!daBolha.length) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {daBolha.map((f) => (
        <div key={f.id} className="rounded-[10px] border border-foreground/12 bg-foreground/2 p-[8px_9px]">
          <Marcador
            marcado={Boolean(f.na_peca)}
            aoAlternar={() => aoAlternar(f.id)}
            rotulo={`${f.rotulo} — entra na peça`}
            estilo="gap:8px"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-apoio leading-[1.4] font-semibold">{f.rotulo}</span>
              <span className="mt-0.5 block text-apoio leading-[1.45] text-tinta-suave">{f.detalhe}</span>
              <span className="mt-1 flex flex-wrap items-center gap-1">
                {f.mensagem_id ? (
                  <Etiqueta
                    discreta
                    render={<button type="button" />}
                    onClick={() => aoVerNaConversa(f.mensagem_id)}
                    data-dica="Ver na conversa a mensagem de onde este fato saiu"
                    className="cursor-pointer underline decoration-dotted underline-offset-2"
                  >
                    {f.fonte || f.origem} ↗
                  </Etiqueta>
                ) : (
                  <Etiqueta discreta>{f.fonte || f.origem}</Etiqueta>
                )}
                <Etiqueta discreta>{f.especie}</Etiqueta>
              </span>
            </span>
          </Marcador>
          {/* Marcar diz "entra quando a IA montar"; inserir escreve AGORA, no
              ponto do cursor. Só com a peça aberta: sem editor, o botão existiria
              para falhar. */}
          {aoInserir ? (
            <div className="mt-1.5">
              <Botao
                rotulo="Inserir na peça"
                titulo="Escreve este trecho no .docx, no ponto do cursor"
                aoTocar={() => aoInserir(f)}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Os trechos do acervo que o comando achou. */
function AchadosDoAcervo({ achados }) {
  if (!achados?.length) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {achados.map((a, i) => (
        <div key={i} className="rounded-[10px] border border-azul/25 bg-azul/6 p-[8px_9px]">
          <div className="font-mono text-selo font-bold tracking-[.06em] text-azul">
            {a.arquivo ?? "peça do acervo"}
            {a.secao ? ` · ${a.secao}` : ""}
          </div>
          <div className="mt-1 text-apoio leading-[1.5] text-tinta-suave [overflow-wrap:anywhere]">
            {String(a.texto ?? "").slice(0, 420)}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Uma bolha da conversa. */
function Bolha({ bolha, provas, aoAplicar, aplicadas, aoAlternarFato, aoVerNaConversa, aoInserirFato }) {
  const pele = peleDe(bolha.papel);
  const edicoes = bolha.dados?.edicoes ?? null;
  return (
    <Message align={pele.lado} className="mb-2">
      <div className={`max-w-[92%] min-w-0 rounded-[13px] border p-[9px_11px] ${pele.classe}`}>
        {bolha.comando && bolha.papel !== "sistema" ? (
          <div className="mb-1 flex items-center gap-1.5 opacity-75">
            <Icone de={GLIFO[bolha.comando] ?? ICONES.ia} tamanho={11} cor="currentColor" />
            <span className="font-mono text-selo font-bold tracking-[.06em]">{bolha.comando.toUpperCase()}</span>
          </div>
        ) : null}

        {/* O escopo que caiu: a pessoa marcou um trecho e a resposta valeu para a
            peça toda. Fica em faixa âmbar, e não no meio do texto, porque é a
            diferença entre "mexeu onde eu pedi" e "mexeu onde quis". */}
        {bolha.dados?.forAPecaInteira ? (
          <div className="mb-1.5 flex items-start gap-1.5 rounded-[8px] bg-ambar/16 p-[6px_8px] text-apoio leading-[1.45] text-ambar-escuro">
            <Icone de={ICONES.ampulheta} tamanho={12} cor="currentColor" />
            <span>Respondi sobre a peça inteira — o trecho marcado não estava no documento salvo.</span>
          </div>
        ) : null}

        <div className="text-corpo leading-[1.5] whitespace-pre-wrap [overflow-wrap:anywhere]">{bolha.texto}</div>

        {/* O trecho que estava marcado quando o pedido saiu: é o que explica, um
            dia depois, por que a IA mexeu ali e não na peça inteira. */}
        {bolha.dados?.trecho ? (
          <div className="mt-1.5 border-l-2 border-l-current/30 pl-2 text-apoio leading-[1.45] opacity-80">
            “{String(bolha.dados.trecho).slice(0, 180)}”
          </div>
        ) : null}

        {bolha.dados?.transcricao ? (
          <div className="mt-1.5 rounded-[8px] bg-black/10 p-[6px_8px] text-apoio leading-[1.45] opacity-90">
            {String(bolha.dados.transcricao).slice(0, 400)}
          </div>
        ) : null}

        {edicoes?.length ? (
          <div>
            {edicoes.map((e, i) => (
              <Edicao
                key={i}
                edicao={e}
                aplicada={aplicadas.has(`${bolha.id}:${i}`)}
                aoAplicar={() => aoAplicar(bolha.id, i, e)}
              />
            ))}
          </div>
        ) : null}

        {bolha.comando === "fatos" ? (
          <FatosDaBolha
            ids={bolha.dados?.fatos}
            provas={provas}
            aoAlternar={aoAlternarFato}
            aoVerNaConversa={aoVerNaConversa}
            aoInserir={aoInserirFato}
          />
        ) : null}

        {bolha.comando === "acervo" ? <AchadosDoAcervo achados={bolha.dados?.achados} /> : null}

        <div className="mt-1.5 text-right font-mono text-selo opacity-60">{hora(bolha.criado_em)}</div>
      </div>
    </Message>
  );
}

/**
 * A coluna do chat.
 *
 * `trecho` vem do editor (a seleção viva) e `aoAplicar` escreve no .docx — os
 * dois são do editor porque é ele que tem o motor; esta coluna só conversa.
 */
export default function ChatDaPeca({
  peca,
  trecho,
  aoSincronizar,
  aoAplicarEdicao,
  aoFechar,
  provas,
  aoAlternarFato,
  aoVerNaConversa,
  aoInserirFato,
  fatoApontado,
}) {
  const { avisar } = useAcoes();
  const ocupado = useOcupado();
  const [bolhas, setBolhas] = useState([]);
  const [comandos, setComandos] = useState([]);
  const [situacao, setSituacao] = useState(null);
  /* Três estados de carga, e cada um tem tela própria: piscar vazio enquanto o
     histórico vem, e "nenhuma conversa" quando o servidor recusou, são a mesma
     tela dizendo duas coisas diferentes. */
  const [carga, setCarga] = useState("carregando");
  const [erroDaCarga, setErroDaCarga] = useState(null);
  const [texto, setTexto] = useState("");
  /* Um comando só, o de escrever: os outros vão pela pastilha, que é onde eles
     são explicados. Guardar "o último comando usado" faria o campo de texto
     mudar de sentido em silêncio. */
  const comando = "ajustar";
  const [enviando, setEnviando] = useState(false);
  const [aplicadas, setAplicadas] = useState(new Set());
  const fim = useRef(null);
  const arquivoRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!peca?.id) return setCarga("sem-peca");
    setCarga((antes) => (antes === "pronto" ? "pronto" : "carregando"));
    try {
      const r = await api.chatDaPeca(peca.id);
      setBolhas(r.bolhas ?? []);
      setComandos(r.comandos ?? []);
      setSituacao(r.situacao ?? null);
      setCarga("pronto");
      setErroDaCarga("");
    } catch (e) {
      /* O erro fica NA COLUNA, com o botão de tentar de novo: balão de seis
         segundos some antes de a pessoa entender, e a coluna vazia depois dele
         parece "esta peça não tem conversa". */
      setErroDaCarga(e);
      setCarga("erro");
    }
  }, [peca?.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /* A conversa nasce no fim, como toda conversa. */
  useEffect(() => {
    fim.current?.scrollIntoView({ block: "end" });
  }, [bolhas.length]);

  const enviar = async (chave = comando) => {
    if (enviando) return;
    const pedido = texto.trim();
    if (chave === "ajustar" && !pedido) return avisar("Escreva o que você quer que a IA faça.");
    setEnviando(true);
    try {
      /*
       * Salvar ANTES de perguntar.
       *
       * A IA lê o .docx gravado. Sem isto, o trecho que a pessoa acabou de
       * marcar pode não existir no texto salvo — e aí o pedido "mexa só aqui"
       * silenciosamente valia para a peça inteira. Foi o que apareceu no
       * primeiro teste desta coluna.
       */
      await aoSincronizar?.();
      const r = await api.pedirNoChat(peca.id, { texto: pedido, comando: chave, trecho: trecho || null });
      setBolhas((antes) => [...antes, ...(r.bolhas ?? [])]);
      setTexto("");
      /* Fatos mudam a lista da peça: quem mostra é a bolha, e ela lê de `provas`. */
      if (chave === "fatos" || chave === "perguntar") await carregar();
    } catch (e) {
      avisar(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const anexar = async (arquivo) => {
    if (!arquivo) return;
    setEnviando(true);
    try {
      const form = new FormData();
      form.append("arquivo", arquivo);
      form.append("nota", texto.trim());
      const r = await api.anexarNoChat(peca.id, form);
      setBolhas((antes) => [...antes, ...(r.bolhas ?? [])]);
      setTexto("");
    } catch (e) {
      avisar(e.message);
    } finally {
      setEnviando(false);
      if (arquivoRef.current) arquivoRef.current.value = "";
    }
  };

  const apontado = (provas ?? []).find((p) => String(p.id) === String(fatoApontado)) ?? null;

  const podeUsar = (c) => {
    if (!situacao) return true;
    if (c.precisa === "peca") return situacao.peca;
    if (c.precisa === "conversa") return situacao.conversa;
    if (c.precisa === "acervo") return situacao.acervo;
    if (c.precisa === "falta") return situacao.falta > 0;
    return true;
  };
  const motivoDe = (c) => (podeUsar(c) ? c.dica : (situacao?.motivos?.[c.precisa] ?? c.dica));

  return (
    /* `h-full` na raiz: sem isso a coluna media o CONTEÚDO, e com a conversa
       vazia o rodapé subia para o meio da faixa com um vão morto embaixo — foi o
       que apareceu no print da peça que ainda não existia. Cabeçalho e rodapé
       são `flex-none`; só o meio rola. */
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-secondary">
      <div className="flex-none border-b border-b-border p-[14px_16px_11px]">
        <div className="flex items-center gap-[9px]">
          <SeloIA />
          <div className="min-w-0 flex-1 font-serif text-[19px]">Conversa sobre a peça</div>
          {aoFechar ? <Fechar aoTocar={aoFechar} titulo="Fechar a petição" /> : null}
        </div>
        {/* A frase que explica o contrato da coluna só enquanto NÃO há conversa:
            com histórico, ela é legenda permanente ocupando duas linhas do topo
            de uma coluna de 380px, todos os dias. */}
        {!bolhas.length ? (
          <div className="mt-1.5 text-apoio leading-[1.5] text-tinta-suave">
            Peça em português. A IA propõe — quem escreve no documento é você, alteração por alteração.
          </div>
        ) : null}
      </div>

      {/* O catálogo, à vista. Cada pastilha diz o que faz na dica, e a que não
          dá para usar agora explica o motivo em vez de ficar apagada e muda. */}
      {comandos.length ? (
        <div className="flex flex-none flex-wrap gap-1.5 border-b border-b-border bg-foreground/2 p-[10px_14px]">
          {comandos.map((c) => (
            <Button
              key={c.chave}
              variant="outline"
              onClick={() => (podeUsar(c) ? enviar(c.chave) : avisar(motivoDe(c)))}
              data-dica={motivoDe(c)}
              aria-disabled={!podeUsar(c)}
              className={`h-auto gap-1.5 rounded-[9px] border-foreground/14 bg-card px-2.5 py-1.5 text-selo font-semibold ${
                podeUsar(c) ? "text-tinta-media" : "opacity-45"
              }`}
            >
              <Icone de={GLIFO[c.chave] ?? ICONES.ia} tamanho={13} cor="currentColor" />
              {c.rotulo}
              {c.chave === "perguntar" && situacao?.falta ? (
                <span className="font-mono text-selo text-primary">{situacao.falta}</span>
              ) : null}
            </Button>
          ))}
        </div>
      ) : null}

      {/* `justify-center` só quando o que está aqui é ESTADO (vazio, carga,
          erro): centrado, o bloco não fica no topo com trezentos pixels de nada
          embaixo. Com conversa, volta a começar em cima e rolar como conversa. */}
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto p-[12px_14px] ${
          carga === "pronto" && bolhas.length ? "" : "justify-center"
        }`}
      >
        {carga === "carregando" ? (
          /* Esqueleto com a FORMA da conversa: três bolhas alternadas. Vazio
             branco enquanto o histórico vem parece "não há nada aqui", e a
             pessoa fecha a coluna antes de ela existir. */
          <Esqueleto linhas={3} altura={64} rotulo="Abrindo a conversa desta peça…" />
        ) : carga === "erro" ? (
          /* A `Ausencia` da casa: ela já traduz status em frase (sessão caída,
             sem acesso, servidor mudo) e traz o botão de tentar de novo. */
          <Ausencia falha={erroDaCarga} alvo="peça" aoTentar={() => void carregar()} compacta />
        ) : carga === "sem-peca" ? (
          /*
           * A peça ainda não existe: DUAS linhas, e não meia tela.
           *
           * Aqui havia um cartão ("A conversa começa com a peça"), um título
           * ("O QUE VAI DAR PARA PEDIR AQUI") e quatro cartões de prévia, mais um
           * rodapé repetindo a mesma frase — cerca de 500px numa coluna de 380
           * para dizer três vezes que a conversa abre quando a peça existir. E
           * esta é a tela em que a pessoa fica trinta segundos, olhando para a
           * ESQUERDA, onde está o que ela veio fazer.
           *
           * O catálogo de comandos não se perde: ele aparece inteiro no momento
           * em que passa a servir, que é com a peça montada e a conversa vazia.
           */
          <div className="px-1 text-center">
            <Icone de={ICONES.peca} tamanho={22} cor="var(--color-tinta-fantasma)" />
            <div className="mt-2 text-corpo leading-[1.55] text-tinta-suave">
              Monte a minuta ao lado. A partir daí esta coluna vira a conversa sobre ESTA peça — ajustar o texto, procurar no
              acervo, perguntar ao cliente — e fica gravada.
            </div>
          </div>
        ) : bolhas.length ? (
          bolhas.map((b) => (
            <Bolha
              key={b.id}
              bolha={b}
              provas={provas}
              aplicadas={aplicadas}
              aoAplicar={(bolhaId, i, edicao) => {
                const ok = aoAplicarEdicao?.(edicao);
                if (ok !== false) setAplicadas((antes) => new Set(antes).add(`${bolhaId}:${i}`));
              }}
              aoAlternarFato={aoAlternarFato}
              aoVerNaConversa={aoVerNaConversa}
              aoInserirFato={aoInserirFato}
            />
          ))
        ) : (
          /*
           * O vazio ensina — UMA vez, e sem repetir a barra logo acima.
           *
           * Eram quatro CARTÕES, um por comando, com o rótulo, a descrição e o
           * exemplo — e os mesmos quatro comandos já estavam desenhados como
           * botões na fileira acima, com a descrição na dica. Numa coluna de
           * 380px isso era ~450px de repetição: oito elementos para quatro
           * ações, e o exemplo em itálico empurrando o campo de escrever para
           * fora da tela.
           *
           * Ficou o que os botões NÃO dizem: o exemplo de como se pede. Uma
           * linha por comando, o rótulo em negrito e a frase entre aspas — que é
           * o que ensina alguém a escrever o próximo pedido com as próprias
           * palavras.
           */
          <div className="px-0.5">
            <div className="mb-2.5 text-corpo leading-[1.55] text-tinta-suave">
              Toque num dos botões acima, ou escreva o pedido. Por exemplo:
            </div>
            <div className="flex flex-col gap-2">
              {comandos.map((c) => (
                <div key={c.chave} className="flex items-start gap-2">
                  <Icone de={GLIFO[c.chave] ?? ICONES.ia} tamanho={13} cor="var(--color-tinta-fantasma)" />
                  <span className="min-w-0 text-apoio leading-[1.5] text-tinta-suave">
                    <span className="font-semibold text-tinta-media">{c.rotulo}: </span>
                    <span className="italic">“{c.exemplo}”</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* O trabalho em voo tem bolha própria, com o relógio andando: a IA leva
            de dez a quarenta segundos, e coluna parada nesse tempo parece tela
            travada. */}
        {enviando ? (
          <div className="flex justify-start" aria-live="polite">
            <div className="mt-1 rounded-[13px] border border-foreground/10 bg-papel p-[9px_11px]">
              <Trabalhando />
            </div>
          </div>
        ) : null}

        <div ref={fim} />
      </div>

      <div className="flex-none border-t border-t-border bg-muted p-[10px_12px_12px]">
        {/* O fato que a conversa apontou (“→ FATO NA PETIÇÃO”): fica FIXO aqui,
            e não perdido no meio da conversa — quem clicou lá veio ver este. */}
        {apontado ? (
          <div className="mb-2 rounded-[10px] border border-verde-fundo/35 bg-verde-fundo/7 p-[8px_10px]">
            <div className="flex items-center gap-1.5 font-mono text-selo font-bold tracking-[.06em] text-verde-fundo">
              <Icone de={ICONES.nota} tamanho={11} cor="currentColor" /> FATO APONTADO NA CONVERSA
            </div>
            <div className="mt-1 text-apoio leading-[1.4] font-semibold">{apontado.rotulo}</div>
            <div className="mt-0.5 text-apoio leading-[1.45] text-tinta-suave">{apontado.detalhe}</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Botao
                rotulo={apontado.na_peca ? "Tirar da peça" : "Marcar para a peça"}
                aoTocar={() => aoAlternarFato(apontado.id)}
              />
              {aoInserirFato ? <Botao rotulo="Inserir na peça" aoTocar={() => aoInserirFato(apontado)} /> : null}
            </div>
          </div>
        ) : null}

        {/* O trecho marcado, à vista ANTES de enviar: é o "isto" de "reescreva
            isto", e sem mostrá-lo a pessoa pede no escuro. */}
        {trecho ? (
          <div className="mb-2 rounded-[10px] border border-verde-fundo/30 bg-verde-fundo/8 p-[7px_9px]">
            <div className="font-mono text-selo font-bold tracking-[.06em] text-verde-fundo">TRECHO MARCADO NA PEÇA</div>
            <div className="mt-1 line-clamp-2 text-apoio leading-[1.45] text-tinta-suave">{trecho}</div>
          </div>
        ) : null}

        {carga === "sem-peca" /* Sem peça o rodapé NÃO existe: o miolo da coluna já diz, em duas
             linhas, que a conversa abre com a peça. Aqui havia a mesma frase de
             novo, com moldura tracejada — a terceira vez que esta coluna dizia a
             mesma coisa na mesma tela. Campo desabilitado com explicação é bom;
             explicação repetida três vezes é ruído. */ ? null : (
          <Composer
            valor={texto}
            aoMudar={setTexto}
            aoEnviar={() => void enviar()}
            travado={enviando || !texto.trim()}
            enviando={enviando}
            dicaDoEnvio="Enviar pedido"
            convite={trecho ? "O que fazer com o trecho marcado?" : "Peça um ajuste, uma busca, um fato…"}
            rotulo="Pedido para a IA sobre esta peça"
            esquerda={
              <>
                <input
                  ref={arquivoRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => void anexar(e.target.files?.[0])}
                  aria-label="Anexar arquivo à conversa da peça"
                />
                <BotaoDoComposer
                  icone={ICONES.anexo}
                  dica="Anexar arquivo — áudio e imagem entram já transcritos"
                  rotulo="Anexar arquivo"
                  aoTocar={() => arquivoRef.current?.click()}
                />
              </>
            }
          />
        )}
        {/*
         * O que a IA está fazendo AGORA — e nada mais.
         *
         * Aqui havia a legenda do teclado ("Enter envia · Shift+Enter quebra
         * linha"), que saiu junto com a do outro composer: quem escreve aperta
         * Enter na segunda vez e não relê aquilo nunca. O que ela carregava de
         * útil era o pedaço do fim — "lendo a conversa…" —, que não é legenda: é
         * o único lugar que diz que a extração está rodando. Fica, sozinho, e só
         * enquanto roda.
         */}
        {ocupado === "fatos" ? (
          <div role="status" className="mt-2 flex items-center gap-2 text-apoio text-verde-fundo">
            <Spinner className="size-3.5" />
            Lendo a conversa inteira para propor os fatos…
          </div>
        ) : null}
      </div>
    </div>
  );
}
